import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-scroll";
import { personalInfo, typingRoles } from "../data";

// Typing animation hook
function useTyping(roles) {
  const [text, setText] = useState("");
  const [roleIndex, setRoleIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const role = roles[roleIndex];
    let timeout;

    if (!deleting && charIndex < role.length) {
      timeout = setTimeout(() => setCharIndex((c) => c + 1), 90);
    } else if (!deleting && charIndex === role.length) {
      timeout = setTimeout(() => setDeleting(true), 1800);
    } else if (deleting && charIndex > 0) {
      timeout = setTimeout(() => setCharIndex((c) => c - 1), 55);
    } else if (deleting && charIndex === 0) {
      setDeleting(false);
      setRoleIndex((r) => (r + 1) % roles.length);
    }

    setText(role.slice(0, charIndex));
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, roleIndex, roles]);

  return text;
}

// Floating particle
function Particle({ style }) {
  return <div className="particle" style={style} />;
}

export default function Hero() {
  const typedText = useTyping(typingRoles);

  // Generate particles
  const particles = Array.from({ length: 20 }, (_, i) => ({
    width: Math.random() * 4 + 2 + "px",
    height: Math.random() * 4 + 2 + "px",
    left: Math.random() * 100 + "%",
    background: i % 3 === 0 ? "#7c6af7" : i % 3 === 1 ? "#06b6d4" : "#a855f7",
    animationDuration: Math.random() * 15 + 10 + "s",
    animationDelay: Math.random() * 10 + "s",
  }));

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-20 pb-12 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-hero-gradient pointer-events-none" />

      {/* Glow orb */}
      <motion.div
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(124,106,247,0.05) 0%, transparent 70%)",
        }}
      />

      {/* Particles */}
      {particles.map((p, i) => (
        <Particle key={i} style={p} />
      ))}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 flex flex-col items-center"
      >
        {/* Badge */}
        <motion.div variants={itemVariants}>
          <div className="inline-flex items-center gap-2 bg-accent-purple/10 border border-accent-purple/25 rounded-full px-4 py-1.5 text-xs font-semibold text-accent-purple mb-8">
            <span className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
            Open to opportunities
          </div>
        </motion.div>

        {/* Profile Photo */}
        <motion.div
          variants={itemVariants}
          className="relative w-40 h-40 mx-auto mb-8"
        >
          {/* Outer Ring */}
          <div
            className="absolute inset-[-5px] rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, #7c6af7, #a855f7, #06b6d4, #7c6af7)",
              padding: "3px",
            }}
          >
            <div className="w-full h-full rounded-full bg-dark-900" />
          </div>

          {/* Image */}
          <div className="absolute inset-[3px] rounded-full overflow-hidden border-2 border-dark-900">
            <img
              src="/profile.jpg.jpeg"
              alt="Saniya Mane"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />

            {/* Fallback initials */}
            <div
              className="w-full h-full hidden items-center justify-center text-4xl font-bold text-white"
              style={{
                background:
                  "linear-gradient(135deg, #7c6af7 0%, #a855f7 50%, #06b6d4 100%)",
              }}
            >
              SM
            </div>
          </div>
        </motion.div>

        {/* Name */}
        <motion.h1
          variants={itemVariants}
          className="text-5xl md:text-7xl font-extrabold mb-3"
        >
          Hi, I'm{" "}
          <span className="text-gradient">
            {personalInfo.firstName} {personalInfo.lastName}
          </span>
        </motion.h1>

        {/* Typing */}
        <motion.div variants={itemVariants} className="h-9 mb-5">
          <p className="text-lg md:text-xl text-accent-cyan font-semibold font-mono">
            {typedText}
            <span className="typing-cursor text-accent-purple">|</span>
          </p>
        </motion.div>

        {/* Tagline */}
        <motion.p
          variants={itemVariants}
          className="text-base md:text-lg text-gray-400 max-w-lg mb-2"
        >
          {personalInfo.tagline}
        </motion.p>

        <motion.p
          variants={itemVariants}
          className="text-sm text-gray-600 mb-10"
        >
          {personalInfo.subTagline}
        </motion.p>

        {/* Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-wrap gap-3 justify-center mb-14"
        >
          <Link to="projects" smooth duration={600} offset={-70}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="px-7 py-3 rounded-lg font-semibold text-sm text-white"
              style={{
                background:
                  "linear-gradient(135deg, #7c6af7, #a855f7, #06b6d4)",
              }}
            >
              View Projects
            </motion.button>
          </Link>

          <motion.a
            href={personalInfo.resume}
            download
            whileHover={{ scale: 1.05 }}
            className="px-7 py-3 rounded-lg font-semibold text-sm text-white border border-white/15 hover:border-accent-purple/60 hover:text-accent-purple"
          >
            Download Resume
          </motion.a>

          <Link to="contact" smooth duration={600} offset={-70}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="px-7 py-3 rounded-lg font-semibold text-sm text-white border border-white/15 hover:border-accent-cyan/60 hover:text-accent-cyan"
            >
              Contact Me
            </motion.button>
          </Link>
        </motion.div>

        {/* Scroll */}
        <motion.div
          variants={itemVariants}
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center text-gray-600 text-xs"
        >
          <span>Scroll to explore</span>
          <div className="w-5 h-5 border-r-2 border-b-2 border-gray-600 rotate-45 mt-1" />
        </motion.div>
      </motion.div>
    </section>
  );
}